import _ from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'

import CloseIcon from '@mui/icons-material/Close'
import MicNoneIcon from '@mui/icons-material/MicNone'
import RefreshIcon from '@mui/icons-material/Refresh'
import SpeakerOutlinedIcon from '@mui/icons-material/SpeakerOutlined'
import StarIcon from '@mui/icons-material/Star'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'

import colors from '@/assets/scss/_themes-vars.module.scss'
import CheckboxInput from '@/components/ui/inputs/CheckboxInput'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { Tooltip, Typography } from '@mui/material'
import AudioDeviceWaveform from './AudioDeviceWaveform'
import AudioOutputTest from './AudioOutputTest'
import VideoDevicePreview from './VideoDevicePreview'

// import { useLocalStorage } from '@/hooks/useLocalStorage'

export type DeviceProps = {
  id: string
  label: string
  iconUrl?: string
  isCustom: boolean
  isSelected: boolean
  type: MediaDeviceKind
}

export type DeviceSelectionDialogProps = {
  /**
   * The device list
   */
  devices?: DeviceProps[]
  onClose: () => void
  /**
   * Callback fired when a device is selected
   */
  onSelectDevice?: (deviceId: string, deviceType: MediaDeviceKind, saveDevice?: boolean) => void
  open: boolean
  showCustomDevices?: boolean
  /**
   * if `true`, audio input setting is shown
   * @default true
   */
  showAudioInput?: boolean
  /**
   * if `true`, audio ouput setting is shown
   * @default true
   */
  showAudioOutput?: boolean
  /**
   * if `true`, video input setting is shown
   * @default true
   */
  showVideoInput?: boolean
  /**
   * The title content
   * @default Devices
   */
  title?: string

  /**
   * if `true`, the selected device will be saved in the local storage
   */
  saveChoice?: boolean
  /**
   * if `true`, shows a hint to tap the stethoscope to identify the device
   * @default false
   */
  showStethoscopeHint?: boolean
  /**
   * Context identifier for scoping last-selected device memory.
   * e.g. 'stethoscope', 'medicalVideo', 'visioConference'
   */
  context?: string
}

export function DeviceSelectionDialog(props: DeviceSelectionDialogProps) {
  const {
    devices = [],
    onClose,
    onSelectDevice,
    open,
    showCustomDevices = true,
    showAudioInput = true,
    showAudioOutput = true,
    showVideoInput = true,
    title = 'Devices',
    saveChoice,
    showStethoscopeHint = false,
    context,
  } = props
  const { t } = useTranslation()
  const isSmallScreen = useIsMobile()

  const [selectedTab, setSelectedTab] = useState(showCustomDevices ? 'custom-devices' : 'media-devices')

  const getLastSelectedDeviceId = useCallback((type: MediaDeviceKind): string | null => {
    if (!context) return null
    try { return localStorage.getItem(`lastSelectedDevice_${context}_${type}`) } catch { return null }
  }, [context])

  const setLastSelectedDeviceId = useCallback((type: MediaDeviceKind, deviceId: string) => {
    if (!context) return
    try { localStorage.setItem(`lastSelectedDevice_${context}_${type}`, deviceId) } catch { /* noop */ }
  }, [context])

  const handleRefreshDevices = useCallback(() => {
    navigator.mediaDevices.dispatchEvent(new Event('devicechange'))
  }, [])
  const [saveDevice, setSaveDevice] = useState<boolean>(saveChoice || false)
  const deviceSettings: { name: MediaDeviceKind; icon: React.ReactNode; label: string; show: boolean }[] = useMemo(
    () => [
      {
        name: 'audioinput',
        icon: <MicNoneIcon />,
        label: t('component.ui.deviceSelectionDialog.deviceKind.audioinput'),
        show: showAudioInput,
      },
      {
        name: 'audiooutput',
        icon: <SpeakerOutlinedIcon />,
        label: t('component.ui.deviceSelectionDialog.deviceKind.audiooutput'),
        show: showAudioOutput,
      },
      {
        name: 'videoinput',
        icon: <VideocamOutlinedIcon />,
        label: t('component.ui.deviceSelectionDialog.deviceKind.videoinput'),
        show: showVideoInput,
      },
    ],
    [showAudioInput, showAudioOutput, showVideoInput, t]
  )
  const [selectedDeviceType, setSelectedDeviceType] = useState<MediaDeviceKind | undefined>(
    _.first(_.filter(deviceSettings, (deviceSetting) => deviceSetting.show))?.name
  )

  const customDevicesMap = useMemo(() => _.groupBy(_.filter(devices, ['isCustom', true]), 'type'), [devices])
  const mediaDevicesMap = useMemo(() => _.groupBy(_.filter(devices, ['isCustom', false]), 'type'), [devices])

  const handleSelectDeviceType = (type: MediaDeviceKind) => () => {
    setSelectedDeviceType(type)
  }

  const handleChangeTab = (evt: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue)
  }

  const handleSelectDevice = (deviceId: string, deviceType: MediaDeviceKind) => {
    setLastSelectedDeviceId(deviceType, deviceId)
    onSelectDevice?.(deviceId, deviceType, saveDevice)
  }

  const renderDevices = (devices: DeviceProps[]) => {
    return _.map(devices, (device) => {
      const showWaveform = device.type === 'audioinput' && open
      const showVideoPreview = device.type === 'videoinput' && open
      const isLastUsed = getLastSelectedDeviceId(device.type) === device.id

      return (
        <Button
          fullWidth
          key={`${device.id} - ${device.type}`}
          onClick={() => handleSelectDevice(device.id, device.type)}
          variant="neutral"
          sx={[
            {
              borderWidth: '2px',
              textAlign: 'left',
              justifyContent: 'flex-start',
              px: 2,
              py: 1,
            },
            device.isSelected && { borderColor: colors.indigo500 },
            !device.isSelected && isLastUsed && { borderColor: colors.indigo200, borderStyle: 'dashed' },
            !!device.iconUrl && !showWaveform && !showVideoPreview && {
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            },
          ]}
        >
          {showWaveform ? (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: '120px', maxWidth: '200px', flexShrink: 0 }}>
                {isLastUsed && <StarIcon sx={{ fontSize: 14, color: colors.indigo400 }} />}
                <Typography variant="body2" noWrap>
                  {device.label}
                </Typography>
              </Stack>
              <AudioDeviceWaveform deviceId={device.id} sx={{ flex: 1 }} />
            </Stack>
          ) : showVideoPreview ? (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
              <VideoDevicePreview deviceId={device.id} />
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
                {isLastUsed && <StarIcon sx={{ fontSize: 14, color: colors.indigo400 }} />}
                <Typography variant="body2" noWrap>
                  {device.label}
                </Typography>
              </Stack>
            </Stack>
          ) : device.type === 'audiooutput' && open ? (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
                {isLastUsed && <StarIcon sx={{ fontSize: 14, color: colors.indigo400 }} />}
                <Typography variant="body2" noWrap>
                  {device.label}
                </Typography>
              </Stack>
              <AudioOutputTest deviceId={device.id} />
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {isLastUsed && <StarIcon sx={{ fontSize: 14, color: colors.indigo400 }} />}
              {device.label}
              {device.iconUrl && <img src={device.iconUrl} alt={device.label} style={{ maxHeight: '150px' }} />}
            </Stack>
          )}
        </Button>
      )
    })
  }

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minHeight: '400px',
        },
      }}
      scroll="paper"
    >
      <Grid container sx={{ flex: 1 }} direction={isSmallScreen ? 'column' : undefined}>
        <Grid item xs={3}>
          <DialogTitle>{title}</DialogTitle>
          <List component="nav">
            {_.map(
              deviceSettings,
              (deviceSetting) =>
                deviceSetting.show && (
                  <ListItemButton
                    key={deviceSetting.name}
                    selected={selectedDeviceType === deviceSetting.name}
                    onClick={handleSelectDeviceType(deviceSetting.name as MediaDeviceKind)}
                  >
                    <ListItemIcon>{deviceSetting.icon}</ListItemIcon>
                    <ListItemText primary={deviceSetting.label} />
                  </ListItemButton>
                )
            )}
          </List>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs>
          <DialogActions>
            <Tooltip title={t('component.ui.deviceSelectionDialog.refreshDevices', { defaultValue: 'Refresh devices' })}>
              <IconButton onClick={handleRefreshDevices}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </DialogActions>
          <TabContext value={selectedTab}>
            <TabList indicatorColor="primary" textColor="primary" variant="fullWidth" onChange={handleChangeTab}>
              {showCustomDevices && <Tab label={t('component.ui.deviceSelectionDialog.tab.customDevices')} value="custom-devices" />}
              <Tab label={t('component.ui.deviceSelectionDialog.tab.mediaDevices')} value="media-devices" />
            </TabList>
            <DialogContent>
              {selectedDeviceType === 'audioinput' && showStethoscopeHint && (
                <Typography variant="body2" align="center" sx={{ color: 'text.secondary', py: 1 }}>
                  {t('component.ui.deviceSelectionDialog.stethoscopeHint')}
                </Typography>
              )}
              {showCustomDevices && (
                <TabPanel value="custom-devices">
                  <Container maxWidth="sm">
                    <Stack spacing={2}>{!!selectedDeviceType && renderDevices(customDevicesMap[selectedDeviceType])}</Stack>
                  </Container>
                </TabPanel>
              )}
              <TabPanel value="media-devices">
                <Container maxWidth="sm">
                  <Stack spacing={2}>{!!selectedDeviceType && renderDevices(mediaDevicesMap[selectedDeviceType])}</Stack>
                </Container>
              </TabPanel>
              <Grid
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                {saveChoice !== undefined && (
                  <CheckboxInput name="saveChoice" label="Save choice" onChange={() => setSaveDevice(!saveDevice)} checked={saveDevice} />
                )}
              </Grid>
            </DialogContent>
          </TabContext>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default DeviceSelectionDialog
